import { OutlinedInputProps } from "@mui/material/OutlinedInput";
import TextField, {
  FilledTextFieldProps,
  OutlinedTextFieldProps,
  StandardTextFieldProps,
} from "@mui/material/TextField";
import React from "react";

import { Controller } from "react-hook-form";
import { InputRoot } from "./InputWithLabelRoot";
import {
  TextareaAutosizeProps,
  InputBaseProps,
  Typography,
  useTheme,
  Box,
  Theme,
  SxProps,
  TextareaAutosize,
  Stack,
} from "@mui/material";
import { useGetCurrentBreakpoint } from "utilz/helpers";

export interface TextAreaProps extends Omit<TextareaAutosizeProps, "size"> {
  size?: "large" | "medium" | "small";
  control?: any;
  name: string;
  label?: React.ReactNode;
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
  /**
   * set to false if fixed input is needed and a size prop can be specified
   */
  isResponsive?: boolean;
  hideHelperBox?: boolean;
  sx?: any;
}

export const TextArea = ({
  size = "medium",
  control,
  name,
  label,
  error = false,
  value,
  errorMessage,
  required = false,
  // variant = "outlined",
  isResponsive = true,
  hideHelperBox = false,
  sx,
  ...rest
}: TextAreaProps) => {
  const theme = useTheme();
  const breakpoint = useGetCurrentBreakpoint(theme);

  if (isResponsive) {
    if (breakpoint === "lg") {
      size = "large";
    } else if (breakpoint === "md") {
      size = "medium";
    } else {
      size = "small";
    }
  }

  return control ? (
    <Controller
      control={control}
      name={name}
      render={({ field: { ref, onChange, onBlur, value, name } }) => (
        <CustomInput
          ref={ref}
          onBlur={onBlur}
          value={value}
          name={name}
          onChange={onChange}
          size={size}
          errorMessage={errorMessage}
          error={error}
          label={label}
          required={required}
          isResponsive={isResponsive}
          hideHelperBox={hideHelperBox}
          sx={sx}
          {...rest}
        />
      )}
    />
  ) : (
    <CustomInput
      size={size}
      errorMessage={errorMessage}
      value={value}
      error={error}
      label={label}
      required={required}
      isResponsive={isResponsive}
      sx={sx}
      name={name}
      hideHelperBox={hideHelperBox}
      {...rest}
    />
  );
};

const CustomInput = ({
  size,
  errorMessage,
  value,
  error,
  label,
  required,
  isResponsive,
  sx,
  name,
  hideHelperBox,
  onChange,
  ref,
  ...rest
}: TextAreaProps) => {
  const theme = useTheme();
  return (
    <Stack
      sx={{
        "& .custom-input-area": {
          outline: "none",
          borderRadius: "4px",
          backgroundColor: theme.palette.neutral["005"],
          border: `1px solid ${
            error ? theme.palette.error.main : theme.palette.neutral["015"]
          }`,
          "&:hover": {
            borderColor: error
              ? theme.palette.error.main
              : theme.palette.lightBg.high,
          },
          "&:focus": { border: `1px solid ${theme.palette.primary.main}` },
          ...theme.typography["body-medium"],
          ...sx,
        },
      }}
    >
      <TextareaAutosize
        ref={ref}
        className="custom-input-area"
        autoComplete="off"
        name={name}
        onChange={onChange}
        value={value}
        {...rest}
      />
      <Box
        sx={{
          pl: "14px",
          mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
        }}
      >
        <Typography variant="body-small" color="error">
          {errorMessage}
        </Typography>
      </Box>
    </Stack>
  );
};
